import { Switch as BaseSwitch } from '@base-ui/react/switch';
import type { ReactNode } from 'react';
import { SWITCH } from './registry';

/**
 * An on/off switch, for state that applies the moment it is flipped.
 *
 * WHEN TO REACH FOR THIS RATHER THAN A CHECKBOX. A checkbox states an intention that a form
 * submission will later act on; a switch reports a state of the world and changes it
 * immediately. Using a checkbox for the second leaves the reader looking for a Save button
 * that is not there, and using a switch for the first silently commits a half-filled form.
 *
 * NEVER FOR A TRI-STATE, and this is written down because the temptation is real: the approval
 * control has three positions and a switch stretched to cover them is exactly what
 * design-guardrails forbids. Three options mean a RadioGroup with three labelled entries.
 *
 * Base UI's Switch rather than a styled checkbox, for the same reason the dialog is Base UI's:
 * it carries `role="switch"` with `aria-checked`, keyboard activation, and the disabled and
 * focus states, none of which a `<input type="checkbox">` with a rounded background provides.
 */
export interface SwitchProps {
  id: string;
  /** The sentence beside it. Carried by the primitive so the label is always associated. */
  label: ReactNode;
  /**
   * Keep the label as the accessible name but take it off the screen.
   *
   * For a switch repeated down a table column, where the column header carries the meaning and
   * the visible text would be the same word on every row — but each control still needs a name
   * that says WHICH row it is ("Notify Jane Cole", not a twentieth "Notify"). Mirrors `Field`'s
   * prop of the same name, and uses the same `vh` class, so there is one way to do this.
   */
  labelHidden?: boolean;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  /**
   * What flipping it will do, shown under the label. Worth writing for anything whose effect
   * is not obvious from the label alone — which is most switches that do something real.
   */
  hint?: ReactNode;
}

export function Switch({
  id,
  label,
  labelHidden = false,
  checked,
  onCheckedChange,
  disabled,
  hint,
}: SwitchProps) {
  const hintId = hint === undefined ? undefined : `${id}-hint`;

  return (
    <div className={SWITCH.row}>
      <BaseSwitch.Root
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled ?? false}
        className={SWITCH.control}
        // Points at the hint rather than folding it into the label: the label is the name of
        // the control and should stay short enough to be announced on every focus.
        aria-describedby={hintId}
      >
        <BaseSwitch.Thumb className={SWITCH.thumb} />
      </BaseSwitch.Root>

      <div>
        {/* `htmlFor` on the Root's id. Base UI renders a real focusable element with that id,
            so clicking the words toggles it — an 18px target with adjacent text is not a
            control anybody can hit reliably. */}
        <label htmlFor={id} className={labelHidden ? 'vh' : SWITCH.label}>
          {label}
        </label>
        {hint === undefined ? null : (
          <p id={hintId} className={SWITCH.hint}>
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}
